// SPDX-License-Identifier: AGPL-3.0-or-later

import {MessageAvatar} from '@app/features/channel/components/MessageAvatar';
import {MessageTimeoutIndicator} from '@app/features/channel/components/MessageTimeoutIndicator';
import {MessageUsername} from '@app/features/channel/components/MessageUsername';
import {TimestampWithTooltip} from '@app/features/channel/components/TimestampWithTooltip';
import {UserTag} from '@app/features/channel/components/UserTag';
import type {Guild} from '@app/features/guild/models/Guild';
import type {GuildMember} from '@app/features/member/models/GuildMember';
import type {Message} from '@app/features/messaging/models/Message';
import styles from '@app/features/theme/styles/Message.module.css';
import type {User} from '@app/features/user/models/User';
import * as DateUtils from '@app/features/user/utils/dateFormatting';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import type {MessagePreviewContext} from '@app/vendor/constants/ChannelConstants';
import {observer} from 'mobx-react-lite';
import {useMemo} from 'react';

interface MessageAuthorInfoProps {
	message: Message;
	author: User;
	guild?: Guild;
	member?: GuildMember;
	shouldGroup: boolean;
	shouldAppearAuthorless: boolean;
	messageDisplayCompact: boolean;
	showUserAvatarsInCompactMode: boolean;
	mobileLayoutEnabled: boolean;
	isHovering: boolean;
	formattedDate: string;
	previewContext?: keyof typeof MessagePreviewContext;
	previewOverrides?: {
		usernameColor?: string;
		displayName?: string;
	};
}
export const MessageAuthorInfo = observer((props: MessageAuthorInfoProps) => {
	const {
		message,
		author,
		guild,
		member,
		shouldGroup,
		shouldAppearAuthorless,
		messageDisplayCompact,
		showUserAvatarsInCompactMode,
		mobileLayoutEnabled,
		isHovering,
		formattedDate,
		previewContext,
		previewOverrides,
	} = props;
	const isPreview = useMemo(() => Boolean(previewContext), [previewContext]);
	const timeoutIndicator = <MessageTimeoutIndicator guildId={message.guildId} userId={author.id} />;
	const username = useMemo(
		() => (
			<MessageUsername
				user={author}
				message={message}
				guild={guild}
				member={member}
				className={styles.messageUsername}
				isPreview={isPreview}
				previewColor={previewOverrides?.usernameColor}
				previewName={previewOverrides?.displayName}
			/>
		),
		[author, message, guild, member, isPreview, previewOverrides?.usernameColor, previewOverrides?.displayName],
	);
	const timestampClass = useMemo(
		() => (shouldGroup ? styles.messageTimestampCompactHover : styles.messageTimestampCompact),
		[shouldGroup],
	);
	if (shouldAppearAuthorless) return null;
	if (messageDisplayCompact) {
		if (shouldGroup && mobileLayoutEnabled) return null;
		return (
			<span className={styles.messageAuthorInfoCompact}>
				<TimestampWithTooltip date={message.timestamp} className={timestampClass}>
					<span className={styles.copyOnly}>[</span>
					{DateUtils.getFormattedTime(message.timestamp)}
					<span className={styles.copyOnly}>]</span>
				</TimestampWithTooltip>
				<span className={styles.copyOnly}> </span>
				{author.bot && <UserTag className={styles.userTagCompact} system={author.system} />}
				{showUserAvatarsInCompactMode && (
					<MessageAvatar
						user={author}
						message={message}
						guildId={guild?.id}
						size={16}
						className={styles.messageAvatarCompact}
						isHovering={isHovering}
						isPreview={isPreview}
					/>
				)}
				<span className={styles.messageAuthorPart}>
					{timeoutIndicator}
					{username}
				</span>
				<span className={styles.copyOnly}>: </span>
			</span>
		);
	}
	if (!shouldGroup) {
		const displayName =
			previewOverrides?.displayName || NicknameUtils.getNickname(author, guild?.id, message.channelId);
		const headerAriaLabel = `${displayName}, ${formattedDate}`;
		return (
			<>
				<div className={styles.messageGutterLeft} />
				<MessageAvatar
					user={author}
					message={message}
					guildId={guild?.id}
					size={40}
					className={styles.messageAvatar}
					isHovering={isHovering}
					isPreview={isPreview}
				/>
				<div className={styles.messageGutterRight} />
				<h3 className={styles.messageAuthorInfo} aria-label={headerAriaLabel}>
					<span className={styles.messageAuthorRow} aria-hidden="true">
						<span className={styles.messageAuthorPart}>
							{timeoutIndicator}
							{username}
							{author.bot && <UserTag className={styles.userTagOffset} system={author.system} />}
						</span>
						<TimestampWithTooltip date={message.timestamp} className={styles.messageTimestamp}>
							<span className={styles.authorDashSeparator}>{' \u2014 '}</span>
							{formattedDate}
						</TimestampWithTooltip>
					</span>
				</h3>
			</>
		);
	}
	if (mobileLayoutEnabled) return null;
	return (
		<>
			<div className={styles.messageGutterLeft} />
			<TimestampWithTooltip date={message.timestamp} className={styles.messageTimestampHover}>
				<span className={styles.textSeparator}>[</span>
				{DateUtils.getFormattedTime(message.timestamp)}
				<span className={styles.textSeparator}>]</span>
			</TimestampWithTooltip>
			<div className={styles.messageGutterRight} />
		</>
	);
});
